/**
 * Pure function that determines which non-last items to collapse.
 *
 * Greedily collapses items starting from the middle (indices 1..n-2),
 * then the first item. Each collapsed item's width is replaced with
 * `ellipsisWidth`. Consecutive collapsed items share a single "..."
 * so only the first one's replacement width counts.
 */
export function computeCollapsedIndices(
    naturalWidths: number[],
    containerWidth: number,
    ellipsisWidth: number,
): Set<number> {
    const totalNatural = naturalWidths.reduce((a, b) => a + b, 0);
    if (naturalWidths.length < 2 || totalNatural <= containerWidth) {
        return new Set();
    }

    const lastIdx = naturalWidths.length - 1;

    // Collapse priority: middle items first, then first item.
    const priority: number[] = [];
    for (let i = 1; i < lastIdx; i++) priority.push(i);
    if (lastIdx > 0) priority.push(0);

    const collapsed = new Set<number>();
    let currentTotal = totalNatural;

    for (const idx of priority) {
        if (currentTotal <= containerWidth) break;

        // Adjacent collapsed items merge into one "..." so we save the full width.
        // Otherwise we pay ellipsisWidth for the new "..." element.
        const prevCollapsed = idx > 0 && collapsed.has(idx - 1);
        const saved = prevCollapsed ? naturalWidths[idx] : naturalWidths[idx] - ellipsisWidth;

        if (saved > 0) {
            currentTotal -= saved;
            collapsed.add(idx);
        }
    }

    return collapsed;
}
